import { useMemo } from 'react';

import { get } from 'lodash-es';
import { useSelector } from 'react-redux';

import { defaultSchema } from '@atlaskit/adf-schema/schema-default';
import { renderDocument, TextSerializer } from '@atlaskit/renderer';
import { gridSize } from '@atlaskit/theme';

import { FabricComment } from 'src/components/conversation-provider/types';
import {
  getValidConversationsForFile,
  getLeveledFabricConversations,
  getConversation,
} from 'src/selectors/conversation-selectors';
import { Diff } from 'src/types/pull-request';
import { BucketState } from 'src/types/state';
import { countChunkLines } from 'src/utils/count-diff-lines';
import { getExcessiveDiffSize } from 'src/utils/get-excessive-diff-size';
import { getExcludedPattern } from 'src/utils/get-excluded-pattern';

// These are estimates drawn from styles in the Diff package (`packages/diff`)
export const fileHeaderHeight = 40;
export const estimatedCommentHeight = 76;
export const nestedCommentMargin = gridSize() * 3.5;
export const conversationOverhead = gridSize() * 4;
export const codeLineHeight = gridSize() * 2.5;

const estimateHeight = (
  lines: number,
  chunks = 1,
  conversations = 0,
  comments = 0
) => {
  const chunkHeaderHeight = chunks * codeLineHeight;

  const nestedCommentsSpacing =
    Math.max(0, comments - conversations) * nestedCommentMargin;

  const commentsEstimate =
    conversations * conversationOverhead +
    comments * estimatedCommentHeight +
    nestedCommentsSpacing;

  return (
    lines * codeLineHeight +
    chunkHeaderHeight +
    fileHeaderHeight +
    commentsEstimate
  );
};

const serializer = new TextSerializer(defaultSchema);

type PlaceholderConversationsParams = {
  file: Diff;
  isOpen: boolean;
};

export const usePlaceholderDiffDetails = ({
  file,
  isOpen,
}: PlaceholderConversationsParams) => {
  const conversations = useSelector((state: BucketState) =>
    getValidConversationsForFile(state, file)
  );
  const fabricConversations = useSelector(getLeveledFabricConversations);

  const excessiveDiff = getExcessiveDiffSize(file);
  const excludedPattern = getExcludedPattern(file);

  const isEmpty =
    excessiveDiff ||
    excludedPattern ||
    file.isBinary ||
    file.isFileContentsUnchanged;

  const lines = isEmpty ? 0 : countChunkLines(file);
  const chunks = isEmpty ? 0 : file.chunks.length;

  const { indexableCommentsAndAuthors, numberOfComments } = useMemo(() => {
    let count = 0;
    let indexable;
    if (isOpen) {
      indexable = (conversations || []).map(convo => {
        const convoId = convo.conversationId;
        const fullConversation = getConversation(fabricConversations, convoId);
        const comments = get(
          fullConversation,
          'comments',
          [] as FabricComment[]
        );
        const extractedAuthorAndText = comments.map(comment => {
          count++;
          const adfDoc = get(comment, 'document.adf', {});
          const authorName = comment.createdBy.name;
          return `${authorName} ${renderDocument(adfDoc, serializer).result}`;
        });

        return extractedAuthorAndText;
      });
    }

    return { indexableCommentsAndAuthors: indexable, numberOfComments: count };
  }, [conversations, fabricConversations, isOpen]);

  const estimatedHeight = estimateHeight(
    lines,
    chunks,
    conversations.length,
    numberOfComments
  );

  return { indexableCommentsAndAuthors, estimatedHeight };
};
